import { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CKAN from '../lib/CKAN';
import { Button, Spinner } from 'react-bootstrap';
import { FaChevronDown, FaCopy } from 'react-icons/fa';
import { LayerStyleHelper } from '../util/StyleHelper';
import DataDownload from '../components/DataDownload';
import DownloadStatus from '../components/DownloadStatus';
import { formatTitle } from '../util/TitleHelper';
import ArcGISMiniMap from './ArcGISMiniMap';
import './DatasetPage.css';

const DatasetPage = (props) => {
  const { datasetName } = useParams();
  const navigate = useNavigate();
  const [datasetData, setDatasetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [itemGis, setItemGis] = useState(null);
  const [wmsThumbnail, setWmsThumbnail] = useState('');
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  // ArcGIS-hosted layers aren't on GeoServer, so the WMS thumbnail can't
  // draw them; they get a live mini map instead.
  const isArcGIS = ['ArcGIS', 'ArcGISTile'].includes(itemGis?.service_type);
  const isBoundary =
    itemGis && itemGis?.layer_name.search('boundary') !== -1 ? true : false;
  const isITS =
    itemGis && itemGis?.layer_name.search('ITS:') !== -1 ? true : false;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const dataset = await CKAN.getSpecificDatasetByName(datasetName);
      if (dataset?.status) return;

      if (dataset.length === 1) {
        setDatasetData(dataset[0]);
        const gis_service = props.getGisService(dataset[0]);
        setItemGis(gis_service);
      }

      setLoading(false);
    };

    fetchData();
  }, []);
  const capitalize = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };
  const handleDownload = async () => {
    if (!datasetData) return;
    setLoading(true);
    setDownloadError(null);
    try {
      await DataDownload(datasetData, { onStatus: setDownloadStatus });
    } catch (error) {
      console.error(error);
      setDownloadError(error.message);
    } finally {
      setDownloadStatus(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (itemGis) {
      const layerName = itemGis.layer_name.includes(':')
        ? itemGis.layer_name.split(':')[1]
        : itemGis.layer_name;
      const sld = LayerStyleHelper(itemGis.layer_name);
      let wms_thumbnail;
      if (sld && sld.includes('_std')) {
        wms_thumbnail =
          `${process.env.REACT_APP_API_URL}/Utility/wms_thumbnail?wms_url=` +
          itemGis.service_url +
          `&layer_name=` +
          layerName +
          `&width=400&height=400` +
          `&sld_style=${sld}`;
      } else if (sld) {
        wms_thumbnail =
          `${process.env.REACT_APP_API_URL}/Utility/wms_thumbnail?wms_url=` +
          itemGis.service_url +
          `&layer_name=` +
          sld +
          `&width=400&height=400`;
      } else {
        wms_thumbnail =
          `${process.env.REACT_APP_API_URL}/Utility/wms_thumbnail?wms_url=` +
          itemGis.service_url +
          `&layer_name=` +
          layerName +
          `&width=400&height=400`;
      }
      setWmsThumbnail(wms_thumbnail);
      setThumbnailFailed(false);
    }
  }, [itemGis]);

  const title = datasetData ? formatTitle(datasetData.title) : '';
  const collection = datasetData?.extras?.find(
    (e) => e.key === 'collection_name'
  )?.value;
  const resources = datasetData?.resources || [];
  const formats = [...new Set(resources.map((r) => r.format).filter(Boolean))];
  const tags = datasetData?.tags || [];
  const metadataRows = datasetData
    ? [
        { label: 'State', value: datasetData.state, isState: true },
        {
          label: 'Last updated',
          value: formatCkanDate(datasetData.metadata_modified),
        },
        {
          label: 'Created',
          value: formatCkanDate(datasetData.metadata_created),
        },
        ...(datasetData.extras || [])
          .filter((e) => !HIDDEN_EXTRAS.includes(e.key))
          .map((e) => ({
            label: capitalize(e.key.replace(/_/g, ' ')),
            value: /^\d{4}-\d\d-\d\dT\d\d:\d\d/.test(e.value)
              ? formatCkanDate(e.value)
              : e.value,
          })),
      ].filter((row) => row.value)
    : [];
  // The dataset's CKAN "spatial" extent (lon/lat), a fallback for where to
  // point the mini map.
  const spatialExtent = useMemo(() => {
    const spatial = datasetData?.extras?.find((e) => e.key === 'spatial');
    if (!spatial) return null;
    try {
      const coords = JSON.parse(spatial.value).coordinates.flat(Infinity);
      const xs = coords.filter((_, i) => i % 2 === 0);
      const ys = coords.filter((_, i) => i % 2 === 1);
      return [
        Math.min(...xs),
        Math.min(...ys),
        Math.max(...xs),
        Math.max(...ys),
      ];
    } catch {
      return null;
    }
  }, [datasetData]);
  const showMap = (isArcGIS || wmsThumbnail) && !thumbnailFailed;

  return (
    <div id={'dataset_' + datasetName} className="dataset-page">
      <nav aria-label="Breadcrumb" className="dataset-breadcrumb">
        <a
          href="/catalog"
          onClick={(e) => {
            e.preventDefault();
            navigate('/catalog');
          }}
        >
          Data Catalog
        </a>
        <span aria-hidden="true">›</span>
        <span aria-current="page">{title}</span>
      </nav>

      {!datasetData ? (
        <div className="dataset-loading">
          <Spinner animation="border" variant="success" />
        </div>
      ) : (
        <>
          {/* Two rows that line up across the columns: summary | map, then
              description | resources. Without a map, resources move up
              beside the summary (see DatasetPage.css). */}
          <div
            className={'dataset-grid' + (showMap ? ' dataset-grid-map' : '')}
          >
            <div className="dataset-summary">
              <h1 className="dataset-title">{title}</h1>
              {collection && (
                <span className="dataset-collection">{collection}</span>
              )}
              <dl className="dataset-facts">
                {datasetData.organization?.title && (
                  <div>
                    <dt>Organization:</dt>
                    <dd>{datasetData.organization.title}</dd>
                  </div>
                )}
                <div>
                  <dt>Last updated:</dt>
                  <dd>{formatCkanDate(datasetData.metadata_modified)}</dd>
                </div>
                {tags.length > 0 && (
                  <div>
                    <dt>Tags:</dt>
                    <dd className="dataset-tags">
                      {tags.map((tag) => (
                        <span className="dataset-tag" key={tag.id || tag.name}>
                          {tag.display_name || tag.name}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
              {resources.length > 0 && (
                <div className="dataset-format-chips">
                  <span className="dataset-chip dataset-chip-count">
                    {resources.length}{' '}
                    {resources.length === 1 ? 'resource' : 'resources'}
                  </span>
                  {formats.map((format) => (
                    <span className="dataset-chip" key={format}>
                      {format}
                    </span>
                  ))}
                </div>
              )}
              <div className="dataset-download">
                <Button
                  className="dataset-download-button"
                  onClick={handleDownload}
                  disabled={loading || !itemGis}
                >
                  {loading && (
                    <>
                      <Spinner animation="border" size="sm" />{' '}
                    </>
                  )}
                  Download{isBoundary ? ' GeoJSON' : isITS ? ' GDB' : ' Zip'}
                </Button>
                <DownloadStatus status={downloadStatus} error={downloadError} />
              </div>
            </div>

            {/* Hidden entirely when the preview can't draw this layer. */}
            {showMap && (
              <div className="dataset-map-area">
                {isArcGIS ? (
                  <ArcGISMiniMap
                    gisService={itemGis}
                    title={title}
                    spatialExtent={spatialExtent}
                    onError={() => setThumbnailFailed(true)}
                  />
                ) : (
                  <img
                    className="dataset-map"
                    src={wmsThumbnail}
                    alt={`Map preview of ${title || 'this dataset'}`}
                    onError={() => setThumbnailFailed(true)}
                  />
                )}
              </div>
            )}

            {datasetData.notes && (
              <p className="dataset-description">
                {toPlainText(datasetData.notes)}
              </p>
            )}

            {resources.length > 0 && (
              <section
                className="dataset-resources-area"
                aria-labelledby="dataset-resources-heading"
              >
                <h2
                  id="dataset-resources-heading"
                  className="dataset-section-heading"
                >
                  Data and Resources{' '}
                  <span className="dataset-count">({resources.length})</span>
                </h2>
                <ul className="dataset-resources">
                  {resources.map((resource, i) => (
                    <ResourceItem
                      key={resource.id || resource.url + i}
                      resource={resource}
                    />
                  ))}
                </ul>
              </section>
            )}
          </div>

          {metadataRows.length > 0 && (
            <section
              className="dataset-metadata"
              aria-labelledby="dataset-metadata-heading"
            >
              <h2
                id="dataset-metadata-heading"
                className="dataset-section-heading"
              >
                Metadata Information
              </h2>
              <table className="dataset-metadata-table">
                <tbody>
                  {metadataRows.map((row) => (
                    <tr key={row.label}>
                      <th scope="row">{row.label}</th>
                      <td>
                        {row.isState ? (
                          <span className="dataset-state">
                            {capitalize(row.value)}
                          </span>
                        ) : (
                          row.value
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
};

// Shown elsewhere on the page (collection badge, map), or too large to be
// useful as text (the spatial extent is a whole GeoJSON polygon).
const HIDDEN_EXTRAS = ['collection_name', 'spatial'];

// Some descriptions were ingested as HTML. Show their text rather than raw
// tags (DOMParser doesn't run scripts or load images).
const toPlainText = (notes) => {
  if (!/<[a-z][\s\S]*>/i.test(notes)) return notes;
  const doc = new DOMParser().parseFromString(notes, 'text/html');
  doc.querySelectorAll('br, p, div, li').forEach((el) => el.append('\n'));
  return doc.body.textContent.replace(/\n{3,}/g, '\n\n').trim();
};

// CKAN timestamps are UTC without a zone suffix.
const formatCkanDate = (value) => {
  if (!value) return null;
  const date = new Date(
    /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : value + 'Z'
  );
  if (isNaN(date)) return value;
  return date.toLocaleString('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  });
};

const ResourceItem = ({ resource }) => {
  const [copied, setCopied] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const descriptionId = useId();
  const description = resource.description?.trim()
    ? toPlainText(resource.description.trim())
    : null;
  // The format already shows as a chip, so drop a "[WMS] " style prefix.
  const name = (resource.name || resource.url || '').replace(
    /^\[[^\]]+\]\s*/,
    ''
  );
  const layer =
    resource.wms_layer || resource.wcs_coverage_id || resource.wfs_feature_id;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(resource.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <li className="dataset-resource">
      {resource.format && (
        <span className="dataset-chip dataset-chip-small">
          {resource.format}
        </span>
      )}
      <div className="dataset-resource-body">
        <div className="dataset-resource-name">
          <a href={resource.url} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
          {resource.url && (
            <button
              type="button"
              className="dataset-copy"
              onClick={handleCopy}
              aria-label={`Copy URL for ${name}`}
            >
              {copied ? 'Copied' : <FaCopy aria-hidden="true" />}
            </button>
          )}
        </div>
        {layer && <div className="dataset-resource-layer">Layer: {layer}</div>}
        {description && (
          <>
            <button
              type="button"
              className="dataset-description-toggle"
              aria-expanded={showDescription}
              aria-controls={descriptionId}
              onClick={() => setShowDescription((open) => !open)}
            >
              {showDescription ? 'Hide description' : 'Show description'}
              <FaChevronDown
                aria-hidden="true"
                className={
                  'dataset-description-chevron' +
                  (showDescription ? ' dataset-description-chevron-open' : '')
                }
              />
            </button>
            <p
              id={descriptionId}
              className="dataset-resource-description"
              hidden={!showDescription}
            >
              {description}
            </p>
          </>
        )}
      </div>
    </li>
  );
};

export default DatasetPage;
